import moment from 'moment';

// Default configuration values for calendar rules
export const DEFAULT_CUTOFF_TIME = '16:00';
export const DEFAULT_TIMEZONE = 'America/Chicago';

export interface ShipDateTableEntry {
  orderDay: string;           // "0" to "6" (matches Date.getDay())
  beforeCutoffShipDay: string; // "0" to "6"
  afterCutoffShipDay: string;  // "0" to "6"
}

export interface TransitRule {
  method: string;
  transitDays: number;
}

// Operational days (Mon-Thu = 1,2,3,4) - Generally, the getOperationalDaysFromTable is used
const OPERATIONAL_DAYS = [1, 2, 3, 4];

// Delivery business days (Mon-Fri = 1,2,3,4,5) for counting transit days
// Weekends are blocked separately via undatePattern configuration
const DELIVERY_BUSINESS_DAYS = [1, 2, 3, 4, 5];

/**
 * Extract operational days from the ship date table
 * Operational days = unique target ship days from the table
 * If no table is provided, ALL days (0-6) are operational (no day-of-week restrictions)
 * @param shipDateTable - Array of ship date table entries from configuration
 * @returns Array of operational day numbers (0-6)
 */
export function getOperationalDaysFromTable(
  shipDateTable: ShipDateTableEntry[]
): number[] {
  // If no table provided, ALL days are operational (no day-of-week restrictions)
  // Other validations (disabled dates, patterns) still apply
  if (!shipDateTable?.length) {
    return [0, 1, 2, 3, 4, 5, 6]; // All days operational
  }
  
  // Collect all unique target ship days from the table
  const targetDays = new Set<number>();
  shipDateTable.forEach(entry => {
    targetDays.add(parseInt(entry.beforeCutoffShipDay, 10));
    targetDays.add(parseInt(entry.afterCutoffShipDay, 10));
  });
  
  return Array.from(targetDays).sort((a, b) => a - b);
}

/**
 * Generate all non-operational days for a date range
 * Non-operational = days NOT in the operational days derived from table
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format
 * @param shipDateTable - Array of ship date table entries from configuration
 * @returns Array of date ranges for non-operational days
 */
export function generateNonOperationalDays(
  startDate: string,
  endDate: string,
  shipDateTable: ShipDateTableEntry[]
): Array<{ startDate: string; endDate: string }> {
  const operationalDays = getOperationalDaysFromTable(shipDateTable);
  const result: Array<{ startDate: string; endDate: string }> = [];
  let current = moment(startDate);
  const end = moment(endDate);
  
  while (current.isSameOrBefore(end)) {
    const dayOfWeek = current.day();
    if (!operationalDays.includes(dayOfWeek)) {
      const dateStr = current.format('YYYY-MM-DD');
      result.push({ startDate: dateStr, endDate: dateStr });
    }
    current.add(1, 'days');
  }
  
  return result;
}

/**
 * Build merged date ranges for every calendar day whose weekday (moment().day()) is in `blockedWeekdays`.
 * 0=Sunday … 6=Saturday. Adjacent blocked days are merged into one range.
 */
export function generateRecurringWeekdayBlocks(
  startDate: string,
  endDate: string,
  blockedWeekdays: number[]
): Array<{ startDate: string; endDate: string }> {
  if (!blockedWeekdays?.length) {
    return [];
  }
  const blocked = new Set(blockedWeekdays);
  const result: Array<{ startDate: string; endDate: string }> = [];
  let runStart: string | null = null;
  let lastBlocked: moment.Moment | null = null;
  const end = moment(endDate);

  for (let cur = moment(startDate); cur.isSameOrBefore(end, 'day'); cur = cur.clone().add(1, 'day')) {
    const isBlocked = blocked.has(cur.day());
    if (isBlocked) {
      if (!runStart) {
        runStart = cur.format('YYYY-MM-DD');
      }
      lastBlocked = cur;
    } else if (runStart && lastBlocked) {
      result.push({ startDate: runStart, endDate: lastBlocked.format('YYYY-MM-DD') });
      runStart = null;
      lastBlocked = null;
    }
  }
  if (runStart && lastBlocked) {
    result.push({ startDate: runStart, endDate: lastBlocked.format('YYYY-MM-DD') });
  }
  return result;
}

// Default ship date table (values as strings to match schema)
const DEFAULT_SHIP_DATE_TABLE: ShipDateTableEntry[] = [
  { orderDay: "0", beforeCutoffShipDay: "2", afterCutoffShipDay: "2" }, // Sunday -> Tuesday
  { orderDay: "1", beforeCutoffShipDay: "2", afterCutoffShipDay: "3" }, // Monday -> Tue/Wed
  { orderDay: "2", beforeCutoffShipDay: "3", afterCutoffShipDay: "4" }, // Tuesday -> Wed/Thu
  { orderDay: "3", beforeCutoffShipDay: "4", afterCutoffShipDay: "1" }, // Wednesday -> Thu/Mon
  { orderDay: "4", beforeCutoffShipDay: "1", afterCutoffShipDay: "1" }, // Thursday -> Mon/Mon
  { orderDay: "5", beforeCutoffShipDay: "1", afterCutoffShipDay: "2" }, // Friday -> Mon/Tue
  { orderDay: "6", beforeCutoffShipDay: "2", afterCutoffShipDay: "2" }, // Saturday -> Tuesday
];

const DEFAULT_TRANSIT_RULES: TransitRule[] = [
  { method: 'express', transitDays: 0 },
  { method: 'priority', transitDays: 1 },
  { method: 'standard', transitDays: 2 },
];

/**
 * Get current time in specified timezone
 * @param timezone - IANA timezone string (e.g., 'America/Chicago')
 * @returns Object with hours and minutes
 */
export function getCurrentTimeInTimezone(timezone: string): { hours: number; minutes: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  return {
    hours: parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10),
    minutes: parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10),
  };
}

/**
 * Check if current time is after cut-off time
 * @param cutoffTime - Cut-off time in HH:mm format (e.g., '14:00')
 * @param timezone - IANA timezone string (e.g., 'America/Chicago')
 * @returns true if current time is after cut-off time
 */
export function isAfterCutoffTime(
  cutoffTime: string = DEFAULT_CUTOFF_TIME,
  timezone: string = DEFAULT_TIMEZONE
): boolean {
  const [cutoffHours, cutoffMinutes] = cutoffTime.split(':').map(Number);
  const current = getCurrentTimeInTimezone(timezone);
  
  if (current.hours > cutoffHours) return true;
  if (current.hours === cutoffHours && current.minutes >= cutoffMinutes) return true;
  return false;
}

/**
 * Get current day number (0-6) in specified timezone
 * @param timezone - IANA timezone string (e.g., 'America/Chicago')
 * @returns Day number (0=Sunday, 6=Saturday)
 */
export function getCurrentDayInTimezone(timezone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
  });
  const dayStr = formatter.format(new Date());
  const dayMap: Record<string, number> = { 
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 
  };
  return dayMap[dayStr] ?? 0;
}

/**
 * Get the next occurrence of a day of week (0-6) from today
 * @param targetDay - Target day number (0=Sunday, 6=Saturday)
 * @param timezone - IANA timezone string (e.g., 'America/Chicago')
 * @returns Date string in YYYY-MM-DD format
 */
export function getNextOccurrenceOfDay(targetDay: number, timezone: string): string {
  const today = moment(getCurrentDateInTimezone(timezone));
  const currentDay = today.day();
  
  let daysToAdd = targetDay - currentDay;
  if (daysToAdd <= 0) {
    daysToAdd += 7; // Next week
  }
  
  return today.clone().add(daysToAdd, 'days').format('YYYY-MM-DD');
}

/**
 * Calculate base ship date (for Overnight) from the table
 * @param timezone - IANA timezone string (e.g., 'America/Chicago')
 * @param cutoffTime - Cut-off time in HH:mm format (e.g., '16:00')
 * @param shipDateTable - Array of ship date table entries from configuration
 * @returns Date string in YYYY-MM-DD format
 */
export function calculateBaseShipDate(
  timezone: string,
  cutoffTime: string,
  shipDateTable: ShipDateTableEntry[]
): string {
  const currentDay = getCurrentDayInTimezone(timezone);
  const afterCutoff = isAfterCutoffTime(cutoffTime, timezone);
  
  // Find mapping for current day
  const table = shipDateTable?.length ? shipDateTable : DEFAULT_SHIP_DATE_TABLE;
  const mapping = table.find(m => m.orderDay === String(currentDay));
  
  if (!mapping) {
    // Fallback to default
    const defaultMapping = DEFAULT_SHIP_DATE_TABLE.find(m => m.orderDay === String(currentDay));
    if (!defaultMapping) {
      // Should never happen, but fallback to Monday
      return getNextOccurrenceOfDay(1, timezone);
    }
    const targetDay = afterCutoff 
      ? parseInt(defaultMapping.afterCutoffShipDay, 10) 
      : parseInt(defaultMapping.beforeCutoffShipDay, 10);
    return getNextOccurrenceOfDay(targetDay, timezone);
  }
  
  const targetDay = afterCutoff 
    ? parseInt(mapping.afterCutoffShipDay, 10) 
    : parseInt(mapping.beforeCutoffShipDay, 10);
  
  return getNextOccurrenceOfDay(targetDay, timezone);
}

/**
 * Add transit days (skipping non-operational days and blocked dates)
 * @param baseDate - Base date string in YYYY-MM-DD format
 * @param transitDays - Number of operational days to add
 * @param operationalDays - Array of operational day numbers (0-6), defaults to Mon-Thu
 * @param noShipDates - Array of blocked date ranges to skip during counting
 * @returns Date string in YYYY-MM-DD format
 */
export function addTransitDays(
  baseDate: string, 
  transitDays: number,
  operationalDays: number[] = OPERATIONAL_DAYS,
  noShipDates: Array<{ startDate: string; endDate: string }> = []
): string {
  let date = moment(baseDate);
  let daysAdded = 0;
  
  const isBlocked = (d: string) => noShipDates.some(
    ns => d >= ns.startDate && d <= ns.endDate
  );
  
  while (daysAdded < transitDays) {
    date.add(1, 'days');
    const dateStr = date.format('YYYY-MM-DD');
    if (operationalDays.includes(date.day()) && !isBlocked(dateStr)) {
      daysAdded++;
    }
  }
  
  // Ensure final date is operational and not blocked
  while (!operationalDays.includes(date.day()) || isBlocked(date.format('YYYY-MM-DD'))) {
    date.add(1, 'days');
  }
  
  return date.format('YYYY-MM-DD');
}

/**
 * Calculate final ship date for a shipping method
 * @param baseShipDate - Base ship date string in YYYY-MM-DD format
 * @param shippingMethod - Shipping method name (e.g., 'express', 'priority', 'standard')
 * @param transitRules - Array of transit rules from configuration
 * @param deliveryBusinessDays - Array of business day numbers (0-6) for counting transit days, defaults to Mon-Fri
 * @param noShipDates - Array of blocked date ranges to skip during transit counting
 * @returns Date string in YYYY-MM-DD format
 */
export function calculateShipDateForMethod(
  baseShipDate: string,
  shippingMethod: string,
  transitRules: TransitRule[],
  deliveryBusinessDays: number[] = DELIVERY_BUSINESS_DAYS,
  noShipDates: Array<{ startDate: string; endDate: string }> = []
): string {
  const rules = transitRules?.length ? transitRules : DEFAULT_TRANSIT_RULES;
  const rule = rules.find(r => r.method === shippingMethod) || { transitDays: 0 };
  
  if (rule.transitDays === 0) {
    return baseShipDate;
  }
  
  // If base ship date is blocked, find next available date first before adding transit days
  const availableBaseDate = findNextAvailableDate(baseShipDate, noShipDates);
  
  return addTransitDays(availableBaseDate, rule.transitDays, deliveryBusinessDays, noShipDates);
}

/**
 * Find next available date (skipping blocked dates)
 * Weekends are blocked via undatePattern configuration, not by operational days filtering
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param noShipDates - Array of blocked date ranges (includes weekends from undatePattern)
 * @returns Date string in YYYY-MM-DD format
 */
export function findNextAvailableDate(
  startDate: string,
  noShipDates: Array<{ startDate: string; endDate: string }>
): string {
  let date = moment(startDate);
  
  const isBlocked = (d: string) => noShipDates.some(
    ns => d >= ns.startDate && d <= ns.endDate
  );
  
  while (isBlocked(date.format('YYYY-MM-DD'))) {
    date.add(1, 'days');
  }
  
  return date.format('YYYY-MM-DD');
}

/**
 * Optimized version using Set for O(1) lookups
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param blockedDatesSet - Set of blocked date strings (YYYY-MM-DD format)
 * @param operationalDays - Array of operational day numbers (0-6)
 * @returns Date string in YYYY-MM-DD format
 */
export function findNextAvailableDateFast(
  startDate: string,
  blockedDatesSet: Set<string>,
  operationalDays: number[]
): string {
  let date = moment(startDate);
  
  while (
    !operationalDays.includes(date.day()) || 
    blockedDatesSet.has(date.format('YYYY-MM-DD'))
  ) {
    date.add(1, 'days');
  }
  
  return date.format('YYYY-MM-DD');
}

/**
 * Get current date in specified timezone (YYYY-MM-DD format)
 * @param timezone - IANA timezone string (e.g., 'America/Chicago')
 * @returns Date string in YYYY-MM-DD format
 */
export function getCurrentDateInTimezone(timezone: string = DEFAULT_TIMEZONE): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  return `${year}-${month}-${day}`;
}

/**
 * Converts a UTC timestamp string to specified timezone date string.
 * Always converts to the specified timezone regardless of user's local timezone.
 * Returns a date string normalized to the date as it appears in the specified timezone.
 * @param utcDateString - UTC timestamp string (e.g., '2026-02-16T05:00:00.000Z')
 * @param timezone - IANA timezone string (e.g., 'America/Chicago'), defaults to Central Time
 * @returns ISO string representing the date as it appears in the specified timezone
 */
export function convertUTCToTimezone(utcDateString: string, timezone: string = DEFAULT_TIMEZONE): string {
  // Parse the UTC date string
  const utcDate = new Date(utcDateString);
  
  // Get the date components in the specified timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  // Format the date parts to get YYYY-MM-DD in the specified timezone
  const parts = formatter.formatToParts(utcDate);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  
  // Return ISO string normalized to midnight UTC for the date as it appears in the specified timezone
  // This ensures when formatted as YYYY-MM-DD, it shows the correct date
  // The datepicker will format this correctly since we're preserving the date part
  return `${year}-${month}-${day}T00:00:00.000Z`;
}

/**
 * @deprecated Use convertUTCToTimezone instead. This function is kept for backward compatibility.
 * Converts a UTC timestamp string to Central Time Zone (CT) date string.
 */
export function convertUTCToCentralTime(utcDateString: string): string {
  return convertUTCToTimezone(utcDateString, DEFAULT_TIMEZONE);
}

export interface DateOverride {
  date: string; // YYYY-MM-DD
  open: boolean;
}

/**
 * Normalize the Studio `dateOverrides` group ({ overrideDate, open }) into YYYY-MM-DD dates.
 * Only the date part of the instant is used (time and timezone are ignored); invalid entries are dropped.
 */
export function parseDateOverrides(
  dateOverrides: Array<{ overrideDate?: string; open?: boolean }> | null | undefined
): DateOverride[] {
  if (!Array.isArray(dateOverrides)) {
    return [];
  }

  const result: DateOverride[] = [];
  dateOverrides.forEach(entry => {
    const date = typeof entry?.overrideDate === 'string' ? entry.overrideDate.slice(0, 10) : '';
    if (moment(date, 'YYYY-MM-DD', true).isValid()) {
      result.push({ date, open: entry.open === true });
    }
  });
  return result;
}

function removeDateFromRange(
  range: { startDate: string; endDate: string },
  date: string
): Array<{ startDate: string; endDate: string }> {
  if (date < range.startDate || date > range.endDate) {
    return [range];
  }

  const pieces: Array<{ startDate: string; endDate: string }> = [];
  if (range.startDate < date) {
    pieces.push({ startDate: range.startDate, endDate: moment(date).subtract(1, 'days').format('YYYY-MM-DD') });
  }
  if (range.endDate > date) {
    pieces.push({ startDate: moment(date).add(1, 'days').format('YYYY-MM-DD'), endDate: range.endDate });
  }
  return pieces;
}

/**
 * Apply manual date overrides to a list of blocked date ranges.
 * Open dates are carved out of the ranges, closed dates are added as blocked.
 * A date listed as both open and closed stays blocked.
 */
export function applyDateOverrides(
  blockedRanges: Array<{ startDate: string; endDate: string }>,
  overrides: DateOverride[]
): Array<{ startDate: string; endDate: string }> {
  if (!overrides?.length) {
    return blockedRanges;
  }

  const openDates = overrides.filter(o => o.open).map(o => o.date);
  const closedRanges = overrides
    .filter(o => !o.open)
    .map(o => ({ startDate: o.date, endDate: o.date }));

  const remainingRanges = openDates.reduce(
    (ranges, openDate) => ranges.flatMap(range => removeDateFromRange(range, openDate)),
    blockedRanges
  );

  return [...remainingRanges, ...closedRanges];
}

export class DateHelpers {
  /**
   * formatTime takes a date and returns a fomatted string, for example 12:34PM
   */
  static formatTime = (date: Date) => {
    let hours = date.getHours();
    const meridiemString = hours > 11 ? 'PM' : 'AM';
    if (meridiemString === 'PM' && hours !== 12) {
      hours -= 12;
    }
    return `${hours}:${date.getMinutes()}${meridiemString}`;
  };

  private static formatDayOrMonth = (num: number) => {
    if (num < 10) {
      return '0' + num.toString();
    }
    return num.toString();
  };

  /**
   * formatMonth takes a number representing the month and returns a two digit string, for example 9 => "09"
   */
  static formatMonth = (month: number) => DateHelpers.formatDayOrMonth(month);

  /**
   * formatDay takes a number representing the day and returns a two digit string, for example 9 => "09"
   */
  static formatDay = (day: number) => DateHelpers.formatDayOrMonth(day);

  /**
   * formatDate takes a Date or string representing the date and returns a user-friendly string, for example
   * Mon Mar 28 2022 07:27:13 GMT+0100 (British Summer Time) => 28/03, 07:27AM
   */
  static formatDate = (date: Date | string) => {
    let unformattedDate: Date = date as Date;
    if (typeof date === 'string') {
      unformattedDate = new Date(date);
    }
    return `${DateHelpers.formatDay(unformattedDate.getDay())}/${DateHelpers.formatMonth(
      unformattedDate.getMonth(),
    )}, ${DateHelpers.formatTime(unformattedDate)}`;
  };
}
